import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useFormikContext, useField } from 'formik'
import { useTranslation } from 'react-i18next'
import Alert from 'src/components/shared/ui/Alert'
import { WarningFillIcon } from 'src/components/shared/ui/Icons'
import EnhancedTable from 'src/components/shared/EnhancedTable/EnhancedTable'
import { InputField, SwitchField } from 'src/components/shared/ui/form/fields'
import AutoCompleteField from 'src/components/shared/ui/form/fields/AutoCompleteField'
import RenderDocumentIcon from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/RenderDocumentIcon'
import { documentTableStyles as styles } from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/tables/DocumentoTablesStyles'
import { parseTextFileName } from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/types'
import RemoveInfoButton from 'src/components/views/EscritosLexnet/components/shared/components/RemoveInfoButton'
import { EscritoFormInitialValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { LEXNET_MAX_MAIN_FILE_SIZE } from 'src/constants/lexnet'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { TipoAdjunto } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { useGetSubtipoDocumento } from 'src/hooks/fetch/useCatalogoGeneralFilters'
import { Orden } from 'src/interfaces/Catalog'
import { DocumentoPrincipalType } from 'src/interfaces/EscritosLegales'
import { theme } from 'src/providers/ThemeProvider'

type Props = {
  id: string
}

function DocumentoPrincipalTable({ id }: Props): JSX.Element {
  const [fieldInfo] = useField(id)
  const [documentoPrincipalArchivo] = useField(FormField.DOCUMENTO_PRINCIPAL_ARCHIVO)
  const { setFieldValue } = useFormikContext()
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )

  const { t } = useTranslation()
  const [data, setData] = useState([])
  const [requiredTable, setRequiredTable] = useState<boolean>(true)
  const [isSigned, setIsSigned] = useState<boolean>(false)
  const { tipoMensaje } = useTipoMensajeContext()

  function clear() {
    setFieldValue(
      FormField.DOCUMENTO_PRINCIPAL,
      EscritoFormInitialValues[FormField.DOCUMENTO_PRINCIPAL]
    )
    setFieldValue(
      FormField.DOCUMENTO_PRINCIPAL_ARCHIVO,
      EscritoFormInitialValues[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO]
    )
    setRequiredTable(true)
    setIsSigned(false)
  }

  const checkIsSigned = () => {
    const fileReader = new FileReader()
    fileReader.onloadend = function(e) {
      const textDecoder = new TextDecoder('utf-8')
      const buffer = textDecoder.decode(e.target?.result as ArrayBuffer)
      setIsSigned(buffer.indexOf('/ByteRange') !== -1)
    }
    if (documentoPrincipalArchivo.value?.file.size <= LEXNET_MAX_MAIN_FILE_SIZE) {
      fileReader.readAsArrayBuffer(documentoPrincipalArchivo.value?.file)
    }
  }

  useEffect(
    () => {
      if (fieldInfo.value.length) {
        if (fieldInfo.value[0].archivo) {
          setRequiredTable(false)
          setData(fieldInfo.value)
          checkIsSigned()
        } else {
          setRequiredTable(true)
          setIsSigned(false)
          setData([])
        }
      } else {
        setRequiredTable(true)
        setIsSigned(false)
        setData([])
      }
    },
    // eslint-disable-next-line
    [fieldInfo.value]
  )

  useEffect(
    () => {
      if (isSigned === true) {
        fieldInfo.value[0].sign = false
      } else {
        fieldInfo.value[0].sign = true
      }
    },
    // eslint-disable-next-line
    [isSigned]
  )

  const items = useGetSubtipoDocumento(FormField.DOCUMENTO_PRINCIPAL, [
    {
      tipoMensaje,
      orden: orden.value ? orden.value.orden : null,
      tipoAdjunto: TipoAdjunto.PRINCIPAL
    }
  ])

  const groupByFn = option => (option.tipoDocumento ? option.tipoDocumento.descripcion : '')

  const [organoDestino] = useField(FormField.ORGANO_DESTINO_DESCRIPCION)

  const disabled =
    documentoPrincipalArchivo.value.hash === null ||
    (documentoPrincipalArchivo.value.hash !== null && organoDestino.value === '')

  const columns = [
    {
      Header: 'Adjunto',
      accessor: 'archivo',
      Cell: ({ value }) => {
        return (
          <Box sx={styles.boxFile}>
            <Box sx={styles.boxFileName}>
              <Box sx={styles.documentIcon}>
                <RenderDocumentIcon file={value} multiple={false} />
              </Box>
              {parseTextFileName(value)}
            </Box>
            {isSigned && (
              <Box sx={styles.boxSign}>
                <SwitchField
                  id={`${FormField.DOCUMENTO_PRINCIPAL}.0.sign`}
                  label={t('form.field.documento_principal_firmar')}
                />
              </Box>
            )}
          </Box>
        )
      },
      width: 200
    },
    {
      Header: t('form.field.catalogacion'),
      accessor: (_item: DocumentoPrincipalType, index: boolean) => (
        <Box sx={styles.container}>
          <Box sx={styles.autocomplete}>
            <AutoCompleteField
              id={`${FormField.DOCUMENTO_PRINCIPAL}.${index}.subtipo`}
              items={items}
              label={t('form.field.catalogacion')}
              groupBy={groupByFn}
              groupName="descripcion"
              itemName="descripcion"
              required
              disabled={disabled}
            />
          </Box>
        </Box>
      ),
      width: 350
    },
    {
      Header: t('form.field.descripcion'),
      accessor: (_item: DocumentoPrincipalType, index: boolean) => (
        <Box sx={styles.container}>
          <Box sx={styles.textarea}>
            <InputField
              id={`${FormField.DOCUMENTO_PRINCIPAL}.${index}.descripcion`}
              label={t('form.field.descripcion')}
              maxLength={100}
            />
          </Box>
        </Box>
      ),
      width: 350
    },
    {
      Header: ' ',
      accessor: () => (
        <Box className="cellActions">
          <RemoveInfoButton
            clear={clear}
            firstDialogMessage={t('dialog.message.remove_documento_principal')}
          />
        </Box>
      ),
      width: 55
    }
  ]

  return (
    <div id={id}>
      {isSigned && !fieldInfo.value[0]?.sign && (
        <Alert
          severity="warning"
          icon={<WarningFillIcon sx={{ fontSize: '22px' }} htmlColor={theme.colors.saffron200} />}
          sx={{ margin: '16px 0', alignItems: 'center', '& .MuiAlert-icon': { padding: 0 } }}
        >
          {t('alert.pdf_ya_firmado')}
        </Alert>
      )}
      <EnhancedTable
        columns={columns}
        data={data && Object.keys(data).length > 0 ? data : []}
        toolbar={false}
        variation="form"
        isDocumentSection
        noRegistersMessage={t('components.enhanced_table.no_registers.documento_principal')}
        noRegistersMessageRequired={true}
        required={false}
        footer={false}
      />
    </div>
  )
}

export default DocumentoPrincipalTable
